import {SmallLoader, Tile} from '@kit';

import React from 'react';

import {useWikiHomeQuery} from '../../../generated/types';
import type {WikiArticleLinkFragment} from '../../../generated/types';
import {getApolloError, imageUrl} from '../../../components/utils';
import {Link} from '../../../lib/routes';
import {MapGrid} from '../../maps/map-grid';
import type {WikiInfobox} from '../infobox';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import css from './wiki-home.module.css';

const CITIES = [
	'waterdeep',
	'baldurs-gate',
	'neverwinter',
	'silverymoon',
	'luskan',
	'mithral-hall',
	'gauntlgrym',
	'candlekeep',
	'daggerford',
	'calimport',
	'athkatla',
	'suzail',
];

const TOPICS = [
	{
		title: 'Боги и магия',
		ids: [
			'religion',
			'gods',
			'the-weave',
			'mythals',
			'wild-magic',
			'magic-items',
			'supernatural-gifts-and-psionics',
		],
	},
	{title: 'История и быт', ids: ['a-brief-history', 'realms-timeline', 'time', 'currency', 'adventurers']},
	{
		title: 'Организации',
		ids: [
			'the-lords-alliance',
			'emerald-enclave',
			'red-wizards',
			'war-wizards',
			'knights-of-myth-drannor',
			'order-of-the-gilded-eye',
			'dark-moon',
			'xanathar-guild',
			'bard-colleges',
			'druid-circles',
			'monastic-orders',
			'paladin-orders',
			'knightly-orders-of-faerun',
			'patrons-in-the-realms',
			'wizardly-groups',
			'mercenaries-of-the-north',
		],
	},
	{
		title: 'Справочник Воло по Вотердипу',
		ids: [
			'waterdeep',
			'history-of-waterdeep',
			'wards-of-waterdeep',
			'waterdeep-code-legal',
			'waterdeep-guilds',
			'waterdeep-celebrations',
			'walking-statues',
		],
	},
];

const IDS = [...new Set([...CITIES, ...TOPICS.flatMap((topic) => topic.ids)])];
const LANDS = ['region'];
const PLACE_ORDER = ['city', 'fortress', 'realm', 'region', 'island'];
const PLACES_IN_CARD = 6;

type Card = WikiArticleLinkFragment & {infobox?: unknown};

function cover(article: Card): string | undefined {
	const infobox = (article.infobox || {}) as WikiInfobox;
	if (LANDS.some((type) => article.types?.includes(type))) return infobox.locator?.path;
	return infobox.image?.path || infobox.locator?.path;
}

function placeRank(article: WikiArticleLinkFragment) {
	const rank = PLACE_ORDER.findIndex((type) => article.types?.includes(type));
	return rank < 0 ? PLACE_ORDER.length : rank;
}

const Poster: React.FC<{article: Card}> = ({article}) => {
	const path = cover(article);
	return (
		<Tile
			route="wikiArticle"
			params={{id: article.id}}
			appearance="poster"
			media={
				<span className={css.preview}>
					{path && <img className={css.previewImage} src={imageUrl(path, 400)} alt="" loading="lazy" />}
				</span>
			}
			title={article.title}
		/>
	);
};

const LinkList: React.FC<{articles: WikiArticleLinkFragment[]}> = ({articles}) => (
	<>
		{articles.map((article, index) => (
			<React.Fragment key={article.id}>
				{index > 0 && ' · '}
				<Link route="wikiArticle" params={{id: article.id}}>
					{article.title}
				</Link>
			</React.Fragment>
		))}
	</>
);

export const WikiHome: React.FC = () => {
	const {data, loading, error} = useWikiHomeQuery({variables: {ids: IDS}, ssr: true});

	if (error) return <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>;
	if (loading && !data) {
		return (
			<div className={pageCss.fullSizeBlock}>
				<SmallLoader />
			</div>
		);
	}

	const featured = new Map((data?.featured || []).map((article) => [article.id, article]));
	const pick = (ids: string[]) => ids.map((id) => featured.get(id)).filter((article): article is Card => !!article);
	const withCover = (ids: string[]) => pick(ids).filter((article) => cover(article));
	const world = data?.world;
	const faerun = data?.faerun;
	const lands = (faerun?.children || []).filter(
		(article) => LANDS.some((type) => article.types?.includes(type)) && cover(article),
	);

	return (
		<article className={dashboardCss.readingWrapper}>
			<h1 className={dashboardCss.mainTitle}>Вики по Забытым Королевствам</h1>
			<div className={dashboardCss.page}>
				<div className={css.home}>
					<p className={css.lead}>
						Энциклопедия мира Forgotten Realms: земли и города Фейруна, боги, организации и история — по
						материалам официальных книг D&D.
					</p>
					{world && (
						<p className={css.world}>
							<Link route="wikiArticle" params={{id: world.id}}>
								{world.title}
							</Link>
							{': '}
							<LinkList articles={world.children} />
						</p>
					)}

					{lands.length > 0 && faerun && (
						<section>
							<h2 className={css.heading}>
								<Link route="wikiArticle" params={{id: faerun.id}}>
									Регионы Фейруна
								</Link>
							</h2>
							<ul className={css.grid}>
								{lands.map((land) => {
									const places = [...land.children]
										.sort(
											(a, b) =>
												placeRank(a) - placeRank(b) || a.title.localeCompare(b.title, 'ru'),
										)
										.slice(0, PLACES_IN_CARD);
									return (
										<li key={land.id} className={css.land}>
											<Poster article={land} />
											{places.length > 0 && (
												<p className={css.places}>
													<LinkList articles={places} />
													{land.children.length > places.length && ' …'}
												</p>
											)}
										</li>
									);
								})}
							</ul>
						</section>
					)}

					<section>
						<h2 className={css.heading}>Великие города</h2>
						<ul className={css.grid}>
							{withCover(CITIES).map((city) => (
								<li key={city.id}>
									<Poster article={city} />
								</li>
							))}
						</ul>
					</section>

					<section className={css.topics}>
						{TOPICS.map((topic) => {
							const articles = pick(topic.ids);
							return articles.length ? (
								<div key={topic.title}>
									<h2 className={css.heading}>{topic.title}</h2>
									<ul className={css.list}>
										{articles.map((article) => (
											<li key={article.id}>
												<Link route="wikiArticle" params={{id: article.id}}>
													{article.title}
												</Link>
											</li>
										))}
									</ul>
								</div>
							) : null;
						})}
					</section>

					<section>
						<h2 className={css.heading}>Интерактивные карты</h2>
						<MapGrid />
					</section>
				</div>
			</div>
		</article>
	);
};
